import { logger } from '@/lib/logger';

export interface PersistedCacheEntry<T> {
  v: number;
  data: T;
  updatedAt: number;
}

export function getPersistedCache<T>(
  key: string,
  expectedVersion: number,
  validate: (raw: unknown) => raw is T,
  component: string,
  maxAgeMs?: number
): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) {
      return undefined;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      logger.warn('Failed to read persisted cache from localStorage', {
        component,
        key,
        reason: 'json-parse-error',
        error: String(err),
      });
      return undefined;
    }

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as Record<string, unknown>).v !== 'number'
    ) {
      logger.warn('Failed to read persisted cache from localStorage', {
        component,
        key,
        reason: 'invalid-envelope',
      });
      return undefined;
    }

    const entry = parsed as { v: number; data: unknown; updatedAt?: unknown };

    if (entry.v !== expectedVersion) {
      logger.warn('Failed to read persisted cache from localStorage', {
        component,
        key,
        reason: 'version-mismatch',
      });
      return undefined;
    }

    if (maxAgeMs !== undefined) {
      // Entries written by setPersistedCache always include a finite numeric
      // updatedAt. Missing/invalid values (non-number, NaN, Infinity) and
      // future timestamps (negative age) are treated as expired so a corrupt
      // entry can't bypass the TTL.
      if (
        typeof entry.updatedAt !== 'number' ||
        !Number.isFinite(entry.updatedAt)
      ) {
        logger.warn('Failed to read persisted cache from localStorage', {
          component,
          key,
          reason: 'expired',
        });
        return undefined;
      }
      const ageMs = Date.now() - entry.updatedAt;
      if (ageMs < 0 || ageMs > maxAgeMs) {
        logger.warn('Failed to read persisted cache from localStorage', {
          component,
          key,
          reason: 'expired',
          ageMs,
          maxAgeMs,
        });
        return undefined;
      }
    }

    if (!validate(entry.data)) {
      logger.warn('Failed to read persisted cache from localStorage', {
        component,
        key,
        reason: 'validation-failed',
      });
      return undefined;
    }

    return entry.data;
  } catch (err) {
    logger.warn('Failed to read persisted cache from localStorage', {
      component,
      key,
      reason: 'storage-error',
      error: String(err),
    });
    return undefined;
  }
}

export function setPersistedCache<T>(
  key: string,
  value: T,
  version: number,
  component: string
): void {
  try {
    // Compare by data payload (ignoring updatedAt) so identical writes
    // don't churn localStorage. Serializing inside the try/catch ensures
    // non-serializable inputs (cyclic refs, BigInts) are caught here
    // instead of bubbling up to callers as uncaught exceptions.
    const newDataSerialized = JSON.stringify(value);
    const current = localStorage.getItem(key);
    if (current !== null) {
      try {
        const parsed = JSON.parse(current) as { v?: unknown; data?: unknown };
        if (
          parsed &&
          typeof parsed === 'object' &&
          parsed.v === version &&
          JSON.stringify(parsed.data) === newDataSerialized
        ) {
          return;
        }
      } catch {
        logger.warn('Failed to compare persisted cache entry', {
          component,
          key,
          reason: 'comparison-parse-error',
        });
        // fall through to write
      }
    }

    const entry: PersistedCacheEntry<T> = {
      v: version,
      data: value,
      updatedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (err) {
    logger.warn('Failed to persist cache to localStorage', {
      component,
      key,
      error: String(err),
    });
  }
}

export function clearPersistedCache(key: string, component: string): void {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    logger.warn('Failed to clear persisted cache from localStorage', {
      component,
      key,
      error: String(err),
    });
  }
}

export function clearAllPersistedCacheByPrefix(
  prefix: string,
  component: string
): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (err) {
    logger.warn('Failed to clear persisted cache entries from localStorage', {
      component,
      prefix,
      error: String(err),
    });
  }
}
